/**
 * WALL COPINGS (2026-10-06, Matt: "cap walls that don't get covered by roofs or extend up to
 * roofs … metal copings, stone copings, brick copings (with corbelling options) … one option
 * for brick that is a sawtooth look where the top course is rotated 45 degrees with a thin
 * brick/tile course running on top of it"). Decisions (asked one at a time):
 *   - set per wall (`coping`), with a TYPE default (`copingDefault`);
 *   - runs only where the wall's top is exposed — skips stretches under / attached to a roof;
 *   - metal: sloped top, legs + drips down both faces;
 *   - stone: SADDLEBACK (ridge in the middle), overhanging both faces;
 *   - brick tops: brick-on-edge (rowlock) · brick-on-edge over two courses of tile CREASING ·
 *     soldier course; 0–3 stepped CORBEL courses below any brick coping (~1" more per course);
 *   - SAWTOOTH = the traditional DOGTOOTH course (bricks laid diagonally at 45°, a corner of
 *     each projecting past both faces) under two half-bonded courses of clay CREASING tiles.
 * The coping sits ON the wall's top — the wall's own height is unchanged. Geometry:
 * bim-geometry categories/wall/coping.ts.
 */
export const COPING_STYLES = ['none', 'metal', 'stoneSaddle', 'brickRowlock', 'brickCreasing', 'brickSoldier', 'brickDogtooth'] as const
export type CopingStyle = (typeof COPING_STYLES)[number]

export const COPING_LABELS: Record<CopingStyle, string> = {
  none: 'None',
  metal: 'Metal — sloped cap, drips both faces',
  stoneSaddle: 'Stone — saddleback',
  brickRowlock: 'Brick — on edge (rowlock)',
  brickCreasing: 'Brick — on edge over tile creasing',
  brickSoldier: 'Brick — soldier course',
  brickDogtooth: 'Brick — sawtooth (dogtooth) under tile creasing',
}

export const COPING_MATERIAL_NAMES = {
  metal: 'Metal Coping, Prefinished',
  stone: 'Limestone Coping',
  tile: 'Clay Creasing Tile',
  brick: 'Brick Veneer',
} as const

const styleOf = (v: unknown): CopingStyle | null =>
  typeof v === 'string' && (COPING_STYLES as readonly string[]).includes(v) ? (v as CopingStyle) : null

/** the wall's coping: its own pick, else its type's default, else none */
export function resolveCopingStyle(p: Record<string, unknown>): CopingStyle {
  return styleOf(p.coping) ?? styleOf(p.copingDefault) ?? 'none'
}
export const isBrickCoping = (s: CopingStyle) => s === 'brickRowlock' || s === 'brickCreasing' || s === 'brickSoldier' || s === 'brickDogtooth'

const STYLE_CHOICES = COPING_STYLES.map((v) => ({ value: v, label: COPING_LABELS[v] }))

/** the wall TYPE's default coping */
export const COPING_TYPE_GROUP = {
  label: 'Coping',
  params: [
    { key: 'copingDefault', label: 'Default Coping', kind: 'choice' as const, default: 'none', choices: STYLE_CHOICES },
  ],
}

/** the wall INSTANCE settings */
export const COPING_INSTANCE_GROUP = {
  label: 'Coping',
  params: [
    {
      key: 'coping', label: 'Coping', kind: 'choice' as const, default: '',
      choices: [{ value: '', label: '(Use Type Default)' }, ...STYLE_CHOICES],
    },
    {
      // brick copings only (ignored by metal / stone)
      key: 'copingCorbel', label: 'Corbel Courses (brick)', kind: 'number' as const, default: 0, min: 0, max: 3,
    },
    {
      // '' = the style's own: brick → the wall's own brick, stone / metal → their firm finishes
      key: 'copingMaterial', label: 'Coping Material', kind: 'material' as const, default: '',
    },
  ],
}
